import { useState } from 'react';
import Menu from '@mui/icons-material/Menu';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { DevTools } from 'features/dev-tools/components/DevTools';
import { selectParticipationDocData } from 'features/participations/store/participationSelectors';
import { useRouteMatch } from 'features/routing/useRouteMatch';
import { useAppSelector } from 'features/store/hooks';
import { ListRow } from 'features/ui';
import { BottomSheet } from './BottomSheet';
import { BrandLogo } from './BrandLogo';
import {
  MOBILE_MORE_ITEMS,
  MOBILE_PRIMARY_ITEMS,
  NAV_PATTERNS,
} from './navItems';

const MORE_VALUE = 'more';

/** Mobile bottom nav: Home · Schedule · More (bottom sheet with the rest). */
export const MobileNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const participation = useAppSelector(selectParticipationDocData);

  const routeMatch = useRouteMatch(NAV_PATTERNS);
  const currentPattern = routeMatch?.pattern.path;
  const activePrimary = MOBILE_PRIMARY_ITEMS.find(
    item => item.route() === currentPattern
  );
  const activeIsMore = MOBILE_MORE_ITEMS.some(
    item => item.route() === currentPattern
  );

  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      <Paper
        variant="outlined"
        elevation={0}
        square
        sx={{
          borderRight: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          flexShrink: 0,
        }}
      >
        <BottomNavigation
          component="nav"
          aria-label={t('ShellNavLabel')}
          showLabels
          value={activeIsMore ? MORE_VALUE : (activePrimary?.route() ?? false)}
          onChange={(_, newValue) => {
            if (newValue === MORE_VALUE) {
              setMoreOpen(true);
            }
          }}
        >
          {MOBILE_PRIMARY_ITEMS.map(item => (
            <BottomNavigationAction
              key={item.id}
              label={t(item.labelKey)}
              icon={<item.Icon />}
              component={Link}
              value={item.route()}
              to={item.route(participation.id)}
              aria-current={
                item.route() === currentPattern ? 'page' : undefined
              }
            />
          ))}
          <BottomNavigationAction
            label={t('More')}
            value={MORE_VALUE}
            icon={<Menu />}
            aria-current={activeIsMore ? 'page' : undefined}
          />
        </BottomNavigation>
      </Paper>
      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2.5,
            pt: 1.5,
            pb: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <BrandLogo />
          <DevTools />
        </Box>
        <Box component="nav" aria-label={t('More')} sx={{ p: '6px 10px 14px' }}>
          {MOBILE_MORE_ITEMS.map(item => {
            const active = item.route() === currentPattern;
            return (
              <ListRow
                key={item.id}
                leading={
                  <item.Icon
                    sx={{
                      fontSize: 22,
                      color: active ? 'primary.main' : 'text.secondary',
                    }}
                  />
                }
                title={t(item.labelKey)}
                onClick={() => {
                  setMoreOpen(false);
                  void navigate(item.route(participation.id));
                }}
                emphasis={active ? 'strong' : 'normal'}
                aria-current={active ? 'page' : undefined}
                sx={{
                  px: 1.5,
                  ...(active && { color: 'primary.main' }),
                }}
              />
            );
          })}
        </Box>
      </BottomSheet>
    </>
  );
};
